import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { SearchCEPAPI, GetMunicipiosByUFAPI } from '@/services/backend/utils'
import { ListFilial, MountCalendar } from '@/services/backend/filial'
import { ListTypesAtendimento, ListStatusAtendimento, CreateSchedulingAdmin, DeleteScheduling, RescheduleAtendimento, GetScheduleHours } from '@/services/backend/Scheduling'
import { GetProductsApi } from '@/services/backend/products'
import { Notification } from '@/plugins/notifications' // Supondo que você tem um sistema de notificações
import request from '@/plugins/admin_axios'
import request_ecommerce from '@/plugins/ecommerce_axios'

export const useAtendimentosStore = defineStore('atendimentos', () => {
  const filialSelecionada = ref(null)
  const filiais = ref([])
  const modalidades = ref([])
  const produtos = ref([])
  const statusAtendimento = ref([])
  const horariosPorDia = ref([])
  const carregando = ref(false)
  const erro = ref(null)

  const getFilialSelecionada = computed(() => filialSelecionada.value)

  const buscarCEP = async (cep) => {
    try {
      return await SearchCEPAPI(cep)
    } catch (error) {
      console.error('Erro ao buscar CEP:', error)
      throw error
    }
  }

  const sanitizeFormData = (data) => {
    return Object.fromEntries(
      Object.entries(data).filter(([, v]) => v !== "" && v !== null)
    )
  }

  const buscarCidades = async (uf) => {
    try {
      const response = await GetMunicipiosByUFAPI(uf)
      return response.data.municipios.map((municipio) => municipio.nome)
    } catch (error) {
      console.error('Erro ao buscar cidades:', error)
      throw error
    }
  }

  const buscarHistoricoStatus = async (uuid) => {
    try {
      return await request.get(`atendimento/historico-status/list/${uuid}`)

    } catch (error) {
      console.error('Erro ao buscar histórico de status:', error)
      throw error
    }
  }

  const buscarDocumentos = async (uuid, side = 'admin') => {
    try {
      const client = side === 'admin' ? request : request_ecommerce
      return await client.get(`atendimento/documentacao/list/${uuid}`)
    } catch (error) {
      console.error('Erro ao buscar documentos:', error)
      throw error
    }
  }

  const removerDocumento = async (uuid_atendimento, uuid_documento, side = 'admin') => {
    const formData = new FormData()
    formData.append('uuid_scheduling', uuid_atendimento)
    formData.append('uuid_document', uuid_documento)

    try {
      const client = side === 'admin' ? request : request_ecommerce
      return await client.delete(`atendimento/documentacao/remove/`, { data: formData })
    } catch (error) {
      console.error('Erro ao remover documento:', error)
      throw error
    }
  }

  const buscarProtocolGateways = async () => {
    try {
      return await request.get(`ticket/list/gateways/`)
    } catch (error) {
      console.error('Erro ao buscar gateways:', error)
      throw error
    }
  }

  const gerarProtocolo = async (atendimento_uuid, gateway_uuid, voucher = null) => {
    try {
      return await request.post(`ticket/generate/`, {
        gateway_uuid: gateway_uuid,
        atendimento_uuid: atendimento_uuid,
        voucher: voucher
      })
    } catch (error) {
      console.error('Erro ao gerar protocolo:', error)
      throw error
    }
  }

  const removerProtocolo = async (atendimento_uuid) => {
    try {
      return await request.delete(`ticket/delete/${atendimento_uuid}`)
    } catch (error) {
      console.error('Erro ao remover protocolo:', error)
      throw error
    }
  }

  const carregarListas = async () => {
    try {
      carregando.value = true
      const [filialRes, modalidadeRes, produtoRes, statusRes] = await Promise.all([ListFilial(true), ListTypesAtendimento(), GetProductsApi(), ListStatusAtendimento()])

      filiais.value = filialRes.data || []
      modalidades.value = modalidadeRes.data || []
      produtos.value = produtoRes.data || []

      statusAtendimento.value = statusRes.data || []
      return { filiais: filiais.value, statusAtendimento: statusAtendimento.value } // Retorna os dados carregados
    } catch (error) {
      console.error('Erro ao carregar listas:', error)
      erro.value = error.message
      return null // Retorna null em caso de erro
    } finally {
      carregando.value = false
    }
  }

  const buscarHorarios = async (dataInicial, dataFinal, filialUuid, status = null) => {
    try {
      carregando.value = true
      const response = await MountCalendar({
        uuid: filialUuid,
        data_inicial: dataInicial,
        data_final: dataFinal,
        status: status || null,
      })

      if (response.status === 'success') {
        return response.data
      } else {
        throw new Error(response.message || 'Erro ao buscar horários')
      }
    } catch (err) {
      erro.value = err.message
    } finally {
      carregando.value = false
    }
  }

  const salvarAtendimento = async (formData, tipoAtendimento, selectedFilial, selectedStatus, dataInicial, dataFinal) => {
    try {
      if (!formData) {
        console.error('Formulário de atendimento não encontrado.')
        return
      }


      if (formData.data_nascimento) {
        const data = new Date(formData.data_nascimento + 'T12:00:00')
        if (!isNaN(data.getTime())) {

          formData.data_nascimento_formatada = formatarDataParaISO(data)

        }
      }

      let atendimento = {
        ...formData,
        data_nascimento: formData.data_nascimento_formatada || formData.data_nascimento,
        is_pre_atendimento: Boolean(tipoAtendimento === 'pre'),
        filial_uuid: selectedFilial,
      }

      delete atendimento.data_nascimento_formatada

      if (!atendimento.atendimento_uuid) delete atendimento.atendimento_uuid


      atendimento = sanitizeFormData(atendimento)
      const response = await CreateSchedulingAdmin(atendimento)

      if (response.status === 'success') {
        Notification.success(response.message)
        if(dataInicial != null && dataFinal != null && selectedFilial != null && selectedStatus != null){
          return await buscarHorarios(dataInicial, dataFinal, selectedFilial, selectedStatus)
        } else {
          return true
        }
      } else {
        Notification.error(response.data.error)
        return false
      }
    } catch (error) {
      Notification.error('Erro ao salvar atendimento: ' + error.response.data.message)
      return false
    }
  }

  const formatarDataParaISO = (data) => {
    if (!data) return null

    const isISO = /^\d{4}-\d{2}-\d{2}$/.test(data)
    if (isISO) {
      return data
    }

    if (data instanceof Date && !isNaN(data)) {
      const day = String(data.getDate()).padStart(2, '0')
      const month = String(data.getMonth() + 1).padStart(2, '0')
      const year = data.getFullYear()
      return `${year}-${month}-${day}`
    }

    const [dia, mes, ano] = data.split('/')
    if (!dia || !mes || !ano) {
      console.error('Formato inválido:', data)
      return null
    }

    return `${ano}-${mes}-${dia}`
  }

  const DeleteAtendimento = async (atendimento, filial, dataInicial, dataFinal) => {
    const response = await DeleteScheduling(atendimento.uuid)
    if (response.status && response.status === 200) {
      return await buscarHorarios(dataInicial, dataFinal, filial)
    } else {
      Notification.error(response.data.error ?? 'Erro ao deletar atendimento. Tente novamente mais tarde.')
      return false
    }
  }

  const Reschedule = async (atendimento_uuid, filial_uuid, data_hora) => {

    try {
      const response = await RescheduleAtendimento(atendimento_uuid, filial_uuid, data_hora)
      if (response.status == 200) {
        Notification.success('Atendimento reagendado com sucesso!')
      }
      return true
    } catch (error) {
      Notification.error(error.response.data?.message ?? 'Erro ao reagendar atendimento. Tente novamente mais tarde.')
      return false
    }
  }

const getScheduleHours = async (filial_uuid, data) => {
  try {
    const response = await GetScheduleHours(filial_uuid, data)
    if (response.status === 200) {
      return response.data;
    } else {
      console.error(response.data);
    }
  } catch (error) {
    console.error("Erro ao horas para agendamento", error);
  }
}

  return {
    filialSelecionada,
    filiais,
    modalidades,
    produtos,
    statusAtendimento,
    horariosPorDia,
    carregando,
    erro,
    buscarCEP,
    removerProtocolo,
    buscarCidades,
    carregarListas,
    buscarHorarios,
    salvarAtendimento,
    DeleteAtendimento,
    Reschedule,
    getScheduleHours,
    buscarHistoricoStatus,
    buscarDocumentos,
    removerDocumento,
    buscarProtocolGateways,
    gerarProtocolo,
    getFilialSelecionada,
  }
},  {
  persist: {
    pick: ['filialSelecionada']
  }
})
